/**
 * Il contesto di accesso, raccolto una volta per chi deve decidere.
 *
 * ## Perché non sta in `navAccess.ts`
 *
 * Quel modulo deve restare **senza hook**: è il dato puro che i test montano per calcolare la
 * matrice «profilo → voci visibili» dei 19 profili del BE
 * (`tests/unit/navMatriceProfili.test.ts`) senza disegnare niente. Se leggesse i claim da sé,
 * quella matrice si potrebbe ottenere solo montando quattro provider.
 *
 * ## Perché non è ricopiato nei quattro punti di consumo
 *
 * Lo era: le stesse tre righe in `DesktopSidebar`, `MobileNavDrawer` e `RouteGate`. Tre copie di
 * un oggetto sono tre posti da cui dimenticarsi un campo — ed è appunto quello che è successo con
 * `claimsPronti`, che è nato dopo e che senza questo hook andrebbe aggiunto a mano tre volte.
 */
import { useCallback, useMemo } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { useTenantConfig } from '@/shared/hooks/useTenantConfig';
import { useAccountMe } from '@/features/auth/useAccountMe';
import { flagAcceso } from '@/shared/lib/tenantModules';
import type { AccessContext } from './navAccess';
import { isNativePlatform } from '@/shared/mobile/useIsMobileShell';

export function useAccessContext(): AccessContext {
  const { decoded, claims, readyClaims, isAuthenticated } = useAuth();
  /*
   * `claims` viene dal contesto, ma la sorgente vera è `decoded.claims`: il contesto lo espone già
   * risolto (col ripiego dei claim persistiti) e questa riga serve a non dipendere da **quale** dei
   * due un chiamante ha in mano. Non è teorico: mezza dozzina di test montano un `useAuth` finto
   * con il solo `decoded`, e un hook che si schianta su un contesto parziale non li sta
   * proteggendo, li sta solo rompendo.
   */
  /* `?? []` produce un array nuovo a ogni render: senza questo `useMemo` la memoizzazione
     qui sotto non memoizza niente, e ricalcola sempre. */
  const claimsList = useMemo(() => claims ?? decoded?.claims ?? [], [claims, decoded?.claims]);
  /*
   * `enabled` sull'autenticazione: questo hook lo monta anche `SpotlightProvider`, che sta **fuori**
   * dalla shell e quindi vive anche sulla pagina di login. Senza la guardia, la lettura di
   * `tenant-attributes` partirebbe senza token — un 401 su una schermata dove non c'e' niente da
   * sbagliare. La guardia sta qui e non dentro `useTenantConfig` perche' quell'hook non deve
   * pretendere un provider di sessione: ce l'aveva, e faceva esplodere ventuno test che montano una
   * pagina senza `AuthProvider`.
   */
  const cfg = useTenantConfig({ enabled: isAuthenticated });
  /*
   * Gli interruttori di modulo si leggono da `/account/me` → `modules`, non da `tenant-attributes`
   * (BE-CONTRACT-TENANT-MODULES-v1 §4). La ragione non e' la velocita' — `/me` e' gia' in cache per
   * il muro dei termini — ma il **claim**: `TenantAttribute.read` a catalogo lo hanno solo HR manager e
   * amministratore. Al dipendente base la lettura di `tenant-attributes` risponde 401, la mappa resta
   * vuota, e ogni flag vale il suo default «acceso»: il menu gli mostrava la flotta spenta, che dal
   * 11-09-2026 gli risponde 403 `MODULE_DISABLED`. `me.modules` invece e' self-service, ed e' la stessa
   * riga che il cancello legge. `tenant-attributes` resta il ripiego, per la finestra in cui `/me` non
   * ha ancora risposto (`foodcost.enabled`, che era fuori catalogo, ci è entrato il 26-10-2026).
   */
  const me = useAccountMe({ enabled: isAuthenticated });
  const modules = me.data?.modules;
  const flagOn = useCallback(
    (flag: string, defaultValue: boolean) => flagAcceso(modules, flag, cfg.getBool, defaultValue),
    [modules, cfg.getBool],
  );
  const isSuperAdmin = decoded?.superadmin === 'True';
  return useMemo(
    () => ({
      claims: claimsList,
      isSuperAdmin,
      /* Non e' uno stato che cambia: una copia dell'applicazione o e' nel guscio o e' nel
         browser, e non passa dall'uno all'altro mentre gira. Quindi si legge e basta. */
      runtime: isNativePlatform() ? 'app' : 'web',
      flagOn,
      /*
       * «Sappiamo che permessi ha» se: è super-admin (passa per regola, come sul server), oppure
       * la sessione lo dichiara, oppure **il token è decodificato** — e quest'ultima non è una
       * ridondanza. `decoded` e i claim si scrivono nello stesso istante (`applyAuthResponse`),
       * quindi un token in mano significa lista definitiva, anche se vuota: un utente senza
       * nessun ruolo non deve vedere il menu intero solo perché la sua lista è vuota.
       */
      readyClaims: isSuperAdmin || readyClaims || Boolean(decoded),
    }),
    [claimsList, isSuperAdmin, flagOn, readyClaims, decoded],
  );
}
